import { useEffect, useRef, useState } from "react"
import { WebsocketProvider } from "y-websocket"
import * as Y from "yjs"

import { CollaborationService } from "services/collaborationService"
import { getCollaborationWsUrl } from "utils/server"
import { useCollaboration } from "features/QuestionnaireEditor/hooks/useCollaboration"
import { Variant } from "types/questionnaire.types"

interface UseCollaborativeSessionProps {
  /** Whether collaboration is enabled (projectId + userId present) */
  collaborationEnabled: boolean
  /** Project ID for collaboration */
  projectId?: string
  /** User ID for collaboration */
  userId?: string
  /** WebSocket URL for collaboration (optional) */
  websocketUrl?: string
  /** The variants data used to pre-initialize all Yjs docs */
  variants: Variant[]
  /** Current selected variant name */
  currentVariant: string
  /** Callback when connection state changes (for disabling parent UI during connection) */
  onConnectionStateChange?: (isConnecting: boolean) => void
}

interface UseCollaborativeSessionReturn {
  isConnected: boolean
  isConnecting: boolean
  isCollaborationReady: boolean
  currentDoc: Y.Doc | null
  setCurrentDoc: React.Dispatch<React.SetStateAction<Y.Doc | null>>
  currentDocRef: React.RefObject<Y.Doc | null>
  pendingVariantSwitchRef: React.RefObject<{ from: string; to: string } | null>
  populatedVariantsRef: React.RefObject<Set<string>>
  switchToVariant: (variantName: string) => Y.Doc | null
  convertToTextFormat: (
    currentContent: string,
    variants: Variant[],
    currentVariant?: string,
    fullQuestionnaire?: string,
  ) => string
  getProvider: (variantName: string) => WebsocketProvider | null
  collaborationService: CollaborationService | null
}

/**
 * useCollaborativeSession manages the collaboration session lifecycle for the
 * questionnaire editor: initializing all variant Yjs docs upfront, tracking
 * connection state (including the initial connection timeout), and detecting
 * variant changes so they can be processed once the editor is available.
 *
 * Everything here is editor-independent; the editor-dependent Yjs effects
 * live in useYjsContentSync.
 */
export const useCollaborativeSession = ({
  collaborationEnabled,
  projectId,
  userId,
  websocketUrl,
  variants,
  currentVariant,
  onConnectionStateChange,
}: UseCollaborativeSessionProps): UseCollaborativeSessionReturn => {
  const currentDocRef = useRef<Y.Doc | null>(null)
  const [currentDoc, setCurrentDoc] = useState<Y.Doc | null>(null)
  const populatedVariantsRef = useRef<Set<string>>(new Set())

  // Collaboration state
  const [isCollaborationReady, setIsCollaborationReady] =
    useState<boolean>(false)
  const [initializationStarted, setInitializationStarted] =
    useState<boolean>(false)
  const [hasConnectedOnce, setHasConnectedOnce] = useState<boolean>(false)

  // Initialize collaboration hook
  const {
    isConnected,
    switchToVariant,
    convertToTextFormat,
    getProvider,
    collaborationService,
  } = useCollaboration({
    projectId: projectId || "",
    userId: userId || "",
    websocketUrl: websocketUrl || getCollaborationWsUrl(),
  })

  // Initialize collaboration when enabled - pre-initialize ALL variants upfront
  // IMPORTANT: Populate Yjs docs BEFORE setting isCollaborationReady to prevent blank editor
  useEffect(() => {
    if (
      !collaborationEnabled ||
      initializationStarted ||
      !collaborationService
    ) {
      return
    }

    setInitializationStarted(true)

    try {
      // Pre-initialize all variants upfront
      // This creates WebSocket connections for all variants, keeping them open
      collaborationService.initializeAllVariants(variants)

      // Switch to the current variant (doc already initialized above)
      const variantName = currentVariant === "" ? "master" : currentVariant
      const doc = switchToVariant(variantName)

      if (doc) {
        currentDocRef.current = doc
        setCurrentDoc(doc)
        setIsCollaborationReady(true)
      } else {
        console.error("Failed to create collaboration doc")
        setInitializationStarted(false)
      }
    } catch (error) {
      console.error("Failed to initialize collaboration:", error)
      setInitializationStarted(false)
    }
  }, [
    collaborationEnabled,
    collaborationService,
    initializationStarted,
    currentVariant,
    variants,
    switchToVariant,
  ])

  // Note: We don't clean up the collaboration service here because:
  // 1. React Strict Mode causes mount/unmount cycles in development
  // 2. The service should persist for the entire project session
  // 3. Cleanup will happen at a higher level (e.g., when closing the project tab)

  // Track when we first successfully connect
  useEffect(() => {
    if (isConnected && !hasConnectedOnce) {
      setHasConnectedOnce(true)
    }
  }, [isConnected, hasConnectedOnce])

  // Connection timeout - if not connected after 5 seconds, stop showing "Connecting..."
  useEffect(() => {
    if (!initializationStarted || hasConnectedOnce || isConnected) {
      return
    }

    const timeout = setTimeout(() => {
      if (!isConnected) {
        // Timeout reached without connecting - treat as if connection attempt is complete
        setHasConnectedOnce(true)
      }
    }, 5000) // 5 second timeout

    return () => clearTimeout(timeout)
  }, [initializationStarted, hasConnectedOnce, isConnected])

  // Determine if we're still connecting
  const isConnecting =
    initializationStarted && !isConnected && !hasConnectedOnce

  // Notify parent of connection state changes
  useEffect(() => {
    onConnectionStateChange?.(isConnecting)
  }, [isConnecting, onConnectionStateChange])

  // Track previous variant to detect changes
  const prevVariantRef = useRef<string>(currentVariant)
  // Store pending variant switch - will be processed after editor is available
  const pendingVariantSwitchRef = useRef<{ from: string; to: string } | null>(
    null,
  )

  // Detect variant changes and queue for processing
  // This effect captures the change but doesn't switch yet
  useEffect(() => {
    if (
      !isCollaborationReady ||
      !collaborationEnabled ||
      !initializationStarted
    ) {
      return
    }

    const variantName = currentVariant === "" ? "master" : currentVariant
    const prevVariant =
      prevVariantRef.current === "" ? "master" : prevVariantRef.current

    // Skip if variant hasn't actually changed
    if (variantName === prevVariant) {
      return
    }

    // Queue the switch - will be processed by the effect that has editor access
    pendingVariantSwitchRef.current = {
      from: prevVariantRef.current, // Keep original value (empty string for master)
      to: currentVariant,
    }
    prevVariantRef.current = currentVariant
  }, [
    currentVariant,
    isCollaborationReady,
    collaborationEnabled,
    initializationStarted,
  ])

  return {
    isConnected,
    isConnecting,
    isCollaborationReady,
    currentDoc,
    setCurrentDoc,
    currentDocRef,
    pendingVariantSwitchRef,
    populatedVariantsRef,
    switchToVariant,
    convertToTextFormat,
    getProvider,
    collaborationService,
  }
}
